import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import AddCaseLawDocumentTypeIndexCategoriesInteractor from "../interactors/AddCaseLawDocumentTypeIndexCategoriesInteractor";
import GetCaseLawDocumentTypeIndexCategoriesInteractor from "../interactors/GetCaseLawDocumentTypeOrganisationIndexCategoriesInteractor";
import AddCaseLawDocumentTypeToOrganisationInteractor from "../interactors/AddCaseLawDocumentTypeToOrganisationInteractor";
import GetCaseLawDocumentTypesForOrganisationInteractor from "../interactors/GetCaseLawDocumentTypesForOrganisationInteractor";
import { IOrganisationIndexCategory } from "../model/OrganisationIndexCategory";
import { IDocumentType } from "../../documentTypes/model/DocumentType";
import DocumentTypeIndexCategory, {
  IDocumentTypeIndexCategory
} from "../../documentTypes/model/DocumentTypeIndexCategory";
import { IOrganisationDocumentType } from "../../documentTypes/model/OrganisationDocumentType";
import { IVisibilityLevel } from "../../documentTypes/model/VisibilityLevel";
import GetAllCaseLawDocumentTypesInteractor from "../../caseLawDocuments/interactor/GetAllCaseLawDocumentTypesInteractor";
import RemoveDocumentCaseLawDocumentTypeInteractor from "../interactors/RemoveDocumentCaseLawDocumentTypeInteractor";

export interface TOrganisationCaseLawDocumentTypesPresenter
  extends TLoadingAwarePresenter {
  organisationId: number;
  organisationCaseLawDocumentTypes: IDocumentType[];
  caseLawDocumentTypes: IDocumentType[];
  modalVisible: boolean;
  assignTypesToOrganisationModalVisible: boolean;
  organisationIndexCategories: IOrganisationIndexCategory[];
  currentCaseLawDocumentTypeId?: number;
  caseLawDocumentTypeIndexCategories: IDocumentTypeIndexCategory[];
  codeList: number[];
}

export interface IOrganisationCaseLawDocumentTypesPresenter
  extends TOrganisationCaseLawDocumentTypesPresenter,
    TPresentable {
  loadReferenceValues(
    organisationId: number,
    organisationIndexCategories: IOrganisationIndexCategory[],
    caseLawDocumentType: IDocumentType[],
    organisationCaseLawDocumentTypes: IDocumentType[]
  ): void;
  getCaseLawDocumentTypeIndexCategories(caseLawDocumentTypeId: number): void;
  addCaseLawDocumentTypeIndexCategories(items: any): void;
  onChangeAddElementsModalVisible(value: boolean, itemId?: number): void;
  addCaseLawDocumentTypeToOrganisation(data: any): void;
  onChangeAddTypesToOrganisationModalVisible(value: boolean): void;
  getCaseLawDocumentTypesForOrganisation(): void;
  loadOrganisationCaseLawDocumentTypes(
    organisationCaseLawDocumentTypes: IDocumentType[],
    caseLawDocumentTypes: IDocumentType[]
  ): void;
  setOrganisationCaseLawDocumentTypeData(id: number): void;
  removeOrganisationCaseLawDocumentType(id: number): void;
}

const defaultState: TOrganisationCaseLawDocumentTypesPresenter = {
  organisationId: NaN,
  organisationCaseLawDocumentTypes: [],
  caseLawDocumentTypeIndexCategories: [],
  modalVisible: false,
  organisationIndexCategories: [],
  currentCaseLawDocumentTypeId: NaN,
  codeList: [],
  caseLawDocumentTypes: [],
  assignTypesToOrganisationModalVisible: false
};

const OrganisationCaseLawDocumentTypesPresenter = withStore<
  IOrganisationCaseLawDocumentTypesPresenter,
  TOrganisationCaseLawDocumentTypesPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationCaseLawDocumentTypesPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TOrganisationCaseLawDocumentTypesPresenter & TLoadingAwarePresenter
    >();
    loader.start("organisationCaseLawDocumentType");
    const loadOrganisationCaseLawDocumentTypes = (
      organisationCaseLawDocumentTypes: IDocumentType[],
      caseLawDocumentTypes: IDocumentType[]
    ) => {
      _store.update({
        organisationCaseLawDocumentTypes,
        caseLawDocumentTypes: caseLawDocumentTypes.filter(
          (item) =>
            !organisationCaseLawDocumentTypes
              .map((el) => el.id)
              .includes(item.id)
        )
      });
    };
    const removeOrganisationCaseLawDocumentType = async (id: number) => {
      loader.start("organisationCaseLawDocumentType");
      const { organisationId } =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>();
      await _application.container
        .resolve<RemoveDocumentCaseLawDocumentTypeInteractor>(
          "removeDocumentCaseLawDocumentType"
        )
        .execute(organisationId, id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      getCaseLawDocumentTypesForOrganisation();
      loader.stop("organisationCaseLawDocumentType");
      return true;
    };
    const loadReferenceValues = (
      organisationId: number,
      organisationIndexCategories: IOrganisationIndexCategory[],
      caseLawDocumentTypes: IDocumentType[],
      organisationCaseLawDocumentTypes: IDocumentType[]
    ) => {
      _store.update({
        organisationCaseLawDocumentTypes,
        caseLawDocumentTypes: caseLawDocumentTypes.filter(
          (item) =>
            !organisationCaseLawDocumentTypes
              .map((el) => el.id)
              .includes(item.id)
        ),
        organisationId,
        organisationIndexCategories
      });
      loader.stop("organisationCaseLawDocumentType");
    };
    const getCaseLawDocumentTypeIndexCategories = async (
      caseLawDocumentTypeId: number
    ) => {
      const { organisationId } =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>();

      let caseLawDocumentTypeIndexCategories = await _application.container
        .resolve<GetCaseLawDocumentTypeIndexCategoriesInteractor>(
          "getCaseLawDocumentTypeIndexCategories"
        )
        .execute(caseLawDocumentTypeId, organisationId);
      _store.update({ caseLawDocumentTypeIndexCategories });
    };
    const addCaseLawDocumentTypeIndexCategories = async (items: any) => {
      const { currentCaseLawDocumentTypeId, organisationId } =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>();

      var itemList = items.fields?.map((item) =>
        DocumentTypeIndexCategory(item)
      );
      currentCaseLawDocumentTypeId &&
        (await _application.container
          .resolve<AddCaseLawDocumentTypeIndexCategoriesInteractor>(
            "addCaseLawDocumentTypeIndexCategories"
          )
          .execute(itemList, currentCaseLawDocumentTypeId, organisationId));
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
    };

    const onChangeAddElementsModalVisible = async (
      value: boolean,
      itemCode?: number
    ) => {
      _store.update({
        modalVisible: value
      });
      if (itemCode) {
        await getCaseLawDocumentTypeIndexCategories(itemCode);
        _store.update({ currentCaseLawDocumentTypeId: itemCode });
      } else {
        _store.update({
          caseLawDocumentTypeIndexCategories: [],
          currentCaseLawDocumentTypeId: undefined
        });
      }
    };
    const onChangeAddTypesToOrganisationModalVisible = async (
      value: boolean
    ) => {
      _store.update({
        assignTypesToOrganisationModalVisible: value
      });
    };

    const addCaseLawDocumentTypeToOrganisation = async (
      item: IDocumentType
    ) => {
      loader.start("organisationCaseLawDocumentType");
      let organisationCaseLawDocumentTypeList: IOrganisationDocumentType[] = [];
      const { organisationId } =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>();

      item.visibilityLevels = item.visibilityLevels?.map((elem) => {
        return {
          status: elem.status,
          valid: true,
          visibilityLevel: elem.visibilityLevel,
          organisationId: organisationId,
          documentTypeId: item.id
        } as IVisibilityLevel;
      });

      await _application.container
        .resolve<AddCaseLawDocumentTypeToOrganisationInteractor>(
          "addCaseLawDocumentTypeToOrganisation"
        )
        .execute(item, organisationId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      _store.update({
        caseLawDocumentTypesForOrganisation: organisationCaseLawDocumentTypeList
      });
      getCaseLawDocumentTypesForOrganisation();
      loader.stop("organisationCaseLawDocumentType");
      return true;
    };

    const setOrganisationCaseLawDocumentTypeData = async (id: number) => {
      const { organisationId, codeList } =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>();

      if (!codeList.includes(id)) {
        codeList.push(id);
      } else {
        codeList.splice(codeList.indexOf(id), 1);
      }

      _store.update({ codeList, organisationId });
    };

    const getCaseLawDocumentTypesForOrganisation = async () => {
      let organisationId =
        _store.getState<TOrganisationCaseLawDocumentTypesPresenter>()
          .organisationId;
      loader.start("organisationCaseLawDocumentType");
      let caseLawDocumentTypesForOrganisation = await _application.container
        .resolve<GetCaseLawDocumentTypesForOrganisationInteractor>(
          "getCaseLawDocumentTypesForOrganisation"
        )
        .execute(organisationId);
      let caseLawDocumentTypes = await _application.container
        .resolve<GetAllCaseLawDocumentTypesInteractor>(
          "getAllCaseLawDocumentTypes"
        )
        .execute();
      _store.update({
        caseLawDocumentTypesForOrganisation,
        organisationId: organisationId
      });
      loadOrganisationCaseLawDocumentTypes(
        caseLawDocumentTypesForOrganisation,
        caseLawDocumentTypes
      );
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadReferenceValues,
      getCaseLawDocumentTypeIndexCategories,
      addCaseLawDocumentTypeIndexCategories,
      onChangeAddElementsModalVisible,
      onChangeAddTypesToOrganisationModalVisible,
      addCaseLawDocumentTypeToOrganisation,
      getCaseLawDocumentTypesForOrganisation,
      setOrganisationCaseLawDocumentTypeData,
      loadOrganisationCaseLawDocumentTypes,
      removeOrganisationCaseLawDocumentType
    };
  },
  defaultState
);

export default OrganisationCaseLawDocumentTypesPresenter;
